/**
 * Everything you have played, and what it says about how you play.
 *
 * Two different bodies of evidence sit on this page and they are NOT interchangeable:
 *
 *  - The **trainer report** (`/api/me/leaks`) reads decisions graded against solved
 *    charts. It is the only half allowed to use the word "solved", and it is the half
 *    that is gated, because it is the thing people pay for.
 *  - The **table report** (`/api/game/sessions`) reads hands played against the bots.
 *    Its "avoidable loss" is our heuristic's estimate — the same one the bots decide
 *    with — and it is described that way everywhere it appears.
 *
 * They are fetched independently and rendered independently. Someone who has only
 * trained and never sat at the table gets the first; someone who has only played gets
 * the second; a failure of either leaves the other on screen. The earlier version
 * early-returned on an empty session list, which hid a full trainer report from
 * anyone who had not yet played a hand.
 *
 * Per-session numbers live on the table screen. This one reads across everything,
 * which is the only scale at which most of these statistics mean anything: a three-bet
 * percentage over twenty opportunities is either 0% or 5%, and a report that
 * confidently interprets that is worse than no report.
 */
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import {
  api, ApiError,
  type GameSessionRow, type GameTotals,
  type LeakReportResponse, type LeakSegment,
} from "../lib/api";
import { emptyStats, derive, findings, strengths, type Finding, type Strength } from "@engine/stats";
import { Link } from "react-router-dom";
import { Card, Skeleton } from "../components/ui";
import {
  AXIS_TITLE, orderSegments, axesIn, groupByAxis,
  pct, pctScaled, fmtEv, fmtEvShort, STRONG,
} from "../lib/report-view";

/** One fetch's outcome, so a half-loaded page is representable rather than guessed at. */
type Load<T> = { state: "loading" } | { state: "ok"; data: T } | { state: "err"; message: string };

function humanise(e: unknown, fallback: string): string {
  /**
   * `ApiError`, not `Error`. ApiError extends Error, so an `e instanceof Error` check
   * is always true and shows the raw wire string — "500 request_failed" — to the
   * player. `humane` is the sentence written for them.
   */
  return e instanceof ApiError ? e.humane : fallback;
}

export function LeakReport() {
  const { getToken } = useAuth();
  const [game, setGame] = useState<Load<{ rows: GameSessionRow[]; totals: GameTotals | null }>>({ state: "loading" });
  const [trainer, setTrainer] = useState<Load<LeakReportResponse>>({ state: "loading" });

  /**
   * `getToken` is a fresh closure on every render, so depending on it here fired the
   * effect on every render — and because the effect sets state, that is a fetch loop
   * that never settles. Held in a ref, with the effect depending on nothing. Same
   * remedy as Play.tsx and useMe.ts, for the same reason.
   */
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;

  useEffect(() => {
    let alive = true;
    const t = tokenRef.current;

    api.gameSessions(t)
      .then((r) => { if (alive) setGame({ state: "ok", data: { rows: r.sessions, totals: r.totals } }); })
      .catch((e) => { if (alive) setGame({ state: "err", message: humanise(e, "Could not load your table history.") }); });

    api.leaks(t)
      .then((r) => { if (alive) setTrainer({ state: "ok", data: r }); })
      .catch((e) => { if (alive) setTrainer({ state: "err", message: humanise(e, "Could not load your trainer report.") }); });

    return () => { alive = false; };
  }, []);

  if (game.state === "loading" && trainer.state === "loading") {
    return <Card><h1>Leak report</h1><Skeleton lines={6} /></Card>;
  }

  const hasTrainer = trainer.state === "ok" && (trainer.data.solvedSpots > 0 || trainer.data.modelledSpots > 0);
  const hasGame = game.state === "ok" && game.data.rows.length > 0
    && !!game.data.totals && game.data.totals.hands > 0;

  /** Both empty AND both healthy is the genuine "nothing yet" case, and only that. */
  if (!hasTrainer && !hasGame && game.state !== "err" && trainer.state !== "err") {
    return (
      <Card>
        <h1>Leak report</h1>
        <p className="muted">
          Nothing here yet. Train a few spots, or play a session at the table, and this
          will fill in. Table stats need analytics switched on in your account settings —
          with them off your stats still work while you play, they just stay on your device.
        </p>
        <p><Link className="btn" to="/train">Open the trainer</Link></p>
      </Card>
    );
  }

  return (
    <div className="report">
      <Card>
        <h1>Leak report</h1>
        <p className="muted small">
          Two sets of numbers, kept apart on purpose. The trainer report is measured
          against solved charts. The table report is our model's estimate of how hands
          against the bots went.
        </p>
      </Card>

      {trainer.state === "err" && (
        <Card><h2>Trainer report</h2><p className="err">{trainer.message}</p></Card>
      )}
      {trainer.state === "ok" && <TrainerReport r={trainer.data} />}

      {game.state === "err" && (
        <Card><h2>At the table</h2><p className="err">{game.message}</p></Card>
      )}
      {game.state === "ok" && game.data.totals && game.data.rows.length > 0 && (
        <TableReport rows={game.data.rows} totals={game.data.totals} />
      )}
    </div>
  );
}

/* ======================================================== the trainer report === */

function TrainerReport({ r }: { r: LeakReportResponse }) {
  if (r.solvedSpots === 0) {
    return (
      <Card>
        <h2>Trainer report</h2>
        <p className="muted">
          {r.modelledSpots > 0 ? (
            <>
              You have trained {r.modelledSpots} spot{r.modelledSpots === 1 ? "" : "s"}, but
              all of them were graded against modelled charts. Only solved spots are
              counted here — a verdict built on our model is a verdict about our model.
            </>
          ) : (
            <>No graded spots in the last {r.windowDays} days yet.</>
          )}
        </p>
        <p><Link className="btn" to="/train">Open the trainer</Link></p>
      </Card>
    );
  }

  const byAxis = groupByAxis(r.segments);
  const axes = axesIn(r.segments);

  return (
    <>
      <Card>
        <h2>Trainer report</h2>
        <p className="muted small">
          {r.solvedSpots.toLocaleString()} solved spot{r.solvedSpots === 1 ? "" : "s"} in
          the last {r.windowDays} days.
          {r.modelledSpots > 0 && (
            <>
              {" "}
              {/*
                Named, not hidden. A player who has trained 400 spots and sees 250 here
                deserves to know where the other 150 went rather than assuming the
                counter is broken.
              */}
              A further {r.modelledSpots.toLocaleString()} were graded against modelled
              charts and are left out — nothing here rests on our model.
            </>
          )}
        </p>

        {/*
          A report with plenty of accuracy and no cost ranking is not a broken report,
          and it must not look like one. Decisions recorded before the comparable cost
          measure existed still count towards how OFTEN this player is right; they
          carry nothing that can be ranked. Saying so is better than a page of dashes.
        */}
        {r.unmeasuredSpots > 0 && (
          <p className="muted small">
            {r.unmeasuredSpots === r.solvedSpots
              ? <>None of these spots carry a cost measurement — they were recorded before
                  we started measuring what a mistake was worth. Accuracy below is real;
                  the cost ranking will fill in as you train.</>
              : <>{r.unmeasuredSpots.toLocaleString()} of them predate our cost measurement
                  and count towards accuracy only.</>}
          </p>
        )}

        {r.worst ? (
          <div className="worst">
            <p className="eyebrow">Costing you the most</p>
            <h3>{r.worst.label}</h3>
            <p>
              {pct(r.worst.accuracy)} of your {r.worst.spots} decisions here matched the
              solved chart. Across a hundred spots, the ones that did not give up{" "}
              <strong>{fmtEv(r.worst.evLostPer100)}</strong>.
            </p>
            <p className="muted small">
              Ranked on cost, not on accuracy. Folding a marginal call is a mistake that
              costs almost nothing; calling off a stack that was a clear fold costs the
              tournament. Cost is measured as a share of what was on the table at each
              decision, so a cheap spot and an expensive one can be compared at all.
            </p>
          </div>
        ) : (
          <p className="muted">
            {r.unmeasuredSpots === r.solvedSpots && r.solvedSpots > 0
              ? <>Nothing here can be ranked by cost yet, for the reason above.</>
              : <>Nothing has enough behind it to call a leak yet — a segment needs{" "}
                  {r.minSample} decisions before this will put a verdict on it.</>}
          </p>
        )}
      </Card>

      <StrengthsCard r={r} />

      {r.gated ? (
        <GateCard r={r} />
      ) : (
        <Card>
          <h2>Every segment</h2>
          <p className="muted small">
            Anything under {r.minSample} decisions is shown with its count and no
            verdict. That is not a placeholder — it is the honest reading, and it also
            tells you which parts of the game you have barely trained.
          </p>
          {axes.map((axis) => (
            <section key={axis} className="axis">
              <h3>{AXIS_TITLE[axis] ?? axis}</h3>
              <ul className="segs">
                {orderSegments(axis, byAxis.get(axis) ?? [])
                  .map((s) => (
                    <SegmentRow key={`${s.axis}:${s.key}`} s={s} min={r.minSample}
                                /* The card above already said why every cost is
                                   missing. Repeating it on every row is noise, and
                                   it wraps the column onto two lines. */
                                explained={r.unmeasuredSpots === r.solvedSpots} />
                  ))}
              </ul>
            </section>
          ))}
        </Card>
      )}
    </>
  );
}

/**
 * What the player is already doing well.
 *
 * The bar is ABSOLUTE — 90% accuracy, and costing no more than their own average —
 * so an empty list is a real answer. The obvious implementation is "your best
 * segment", and it is the wrong one: a best segment always exists, so a player losing
 * money everywhere still gets congratulated on something. They can tell, and it costs
 * the rest of the report its credibility.
 */
function StrengthsCard({ r }: { r: LeakReportResponse }) {
  if (r.gated) {
    return (
      <Card>
        <h2>What you're doing well in the trainer</h2>
        <p className="muted">
          {r.withheldStrengths
            ? <>
                {r.withheldStrengths} part{r.withheldStrengths === 1 ? "" : "s"} of your game
                cleared the bar for this. Pro shows which.
              </>
            : <>Nothing has cleared the bar for this yet. Pro shows it as soon as something does.</>}
        </p>
      </Card>
    );
  }

  return (
    <Card>
      {/*
        The heading names its evidence. There is a second "what you're doing well" on
        this page, from the table report, and the two rest on different things: this
        one on decisions graded against solved charts, the other on hand statistics
        our model interprets. Two identical headings would imply one measurement.
      */}
      <h2>What you're doing well in the trainer</h2>
      {r.strengths.length === 0 ? (
        <p className="muted">
          Nothing has cleared the bar yet — that means {Math.round(STRONG * 100)}% accuracy
          over at least {r.minSample} decisions, while costing no more than your own
          average. It is deliberately a high bar, so that when this section says
          something you can believe it.
        </p>
      ) : (
        <>
          <p className="muted small">
            Spots you can trust yourself in. Useful mainly for knowing where NOT to spend
            the next hour.
          </p>
          <ul className="segs strong">
            {r.strengths.map((s) => (
              <li key={`${s.axis}:${s.key}`} className="seg seg-strong">
                <span className="seg-label">{s.label}</span>
                <span className="seg-acc num">{pct(s.accuracy)}</span>
                <span className="seg-note muted small">
                  {s.spots} decisions · {fmtEvShort(s.evLostPer100)} per 100
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}


function GateCard({ r }: { r: LeakReportResponse }) {
  const n = r.withheldSegments ?? 0;
  return (
    <Card>
      <div className="gate">
        <p className="gate-title">The rest of the breakdown is part of Pro</p>
        <p className="muted small">
          {n > 0
            ? <>
                We measured {n} more segment{n === 1 ? "" : "s"} of your play across decision
                type, position, stack depth, tournament stage and table size. Pro shows all
                of them, and which parts of your game are already sound.
              </>
            : <>
                Pro breaks your play down by decision type, position, stack depth,
                tournament stage and table size, and shows which parts are already sound.
              </>}
        </p>
        {/*
          The counts are the whole pitch and they are real numbers from this player's
          own decisions. No sample screenshot of someone else's report, and no blurred
          panel implying a specific verdict is waiting behind it — the withheld
          segments never left the server, so there is nothing here to blur.
        */}
        <Link className="btn-quiet" to="/pricing">See what's included</Link>
      </div>
    </Card>
  );
}

function SegmentRow({ s, min, explained }: {
  s: LeakSegment; min: number; explained: boolean;
}) {
  if (!s.diagnosable) {
    return (
      <li className="seg seg-thin">
        <span className="seg-label">{s.label}</span>
        <span className="seg-acc num muted">—</span>
        <span className="seg-note muted small">
          {s.spots} decision{s.spots === 1 ? "" : "s"} · {min - s.spots} more needed
        </span>
      </li>
    );
  }
  const acc = s.accuracy ?? 0;
  return (
    <li className={`seg ${acc >= STRONG ? "seg-good" : acc < 0.75 ? "seg-poor" : ""}`}>
      <span className="seg-label">{s.label}</span>
      <span className="seg-acc num">{pct(s.accuracy)}</span>
      <span className="seg-note muted small">
        {s.spots} decisions
        {s.evLostPer100 !== null
          ? ` · ${fmtEvShort(s.evLostPer100)} per 100`
          : explained ? "" : " · no cost recorded"}
      </span>
    </li>
  );
}


/* ========================================================== the table report === */

function TableReport({ rows, totals }: { rows: GameSessionRow[]; totals: GameTotals }) {
  /**
   * The totals come back in BIG BLINDS, already divided per session, so a hundred
   * hands at 1/2 and a hundred at 5/10 can sit in the same average. Rebuilding a Stats
   * object from them lets the same rules that run live at the table run here, rather
   * than a second implementation that will eventually disagree with the first.
   */
  const s = emptyStats(1);
  s.hands = totals.hands;
  s.net = totals.net_bb;
  s.vpip = totals.vpip; s.pfr = totals.pfr;
  s.threeBetChances = totals.three_bet_chances; s.threeBets = totals.three_bets;
  s.facedThreeBet = totals.faced_three_bet; s.foldedToThreeBet = totals.folded_to_three_bet;
  s.sawFlop = totals.saw_flop; s.wentToShowdown = totals.showdowns;
  s.wonAtShowdown = totals.showdowns_won; s.wonWhenSawFlop = totals.won_saw_flop;
  s.aggressiveActions = totals.aggressive; s.passiveActions = totals.passive;
  s.cbetChances = totals.cbet_chances; s.cbets = totals.cbets;
  s.facedCbet = totals.faced_cbet; s.foldedToCbet = totals.folded_to_cbet;
  s.allIns = totals.all_ins; s.decisions = totals.decisions;
  s.evLost = totals.ev_lost_bb;
  s.evLostByStreet = {
    preflop: totals.ev_preflop_bb, flop: totals.ev_flop_bb,
    turn: totals.ev_turn_bb, river: totals.ev_river_bb,
  };

  const d = derive(s);
  const leaks: Finding[] = findings(s);
  const good: Strength[] = strengths(s);

  return (
    <>
      <Card>
        <h2>At the table</h2>
        <p className="muted">
          {totals.sessions} session{totals.sessions === 1 ? "" : "s"}, {totals.hands} hands
          against the bots.
        </p>

        <dl className="statgrid">
          <Big label="Won" unit="bb/100"
               value={d.bb100 === null ? "—" : d.bb100.toFixed(1)}
               tone={(d.bb100 ?? 0) >= 0 ? "up" : "down"} />
          <Big label="VPIP / PFR" value={`${pctScaled(d.vpip)} / ${pctScaled(d.pfr)}`} />
          <Big label="3-bet" value={pctScaled(d.threeBet)} />
          <Big label="WTSD" value={pctScaled(d.wtsd)} />
          <Big label="Aggression" value={d.aggressionFactor === null ? "—"
            : d.aggressionFactor === Infinity ? "∞" : d.aggressionFactor.toFixed(1)} />
          <Big label="Avoidable loss" unit="bb/100"
               value={d.evLostPer100 === null ? "—" : d.evLostPer100.toFixed(1)} />
        </dl>
      </Card>

      <Card>
        <h2>Where the money goes</h2>
        <p className="muted small">
          Our model's estimate of what each street cost you, in big blinds per hundred
          hands. It is a heuristic — the same one the bots use to decide — not a solve.
        </p>
        <StreetBars totals={totals} hands={totals.hands} />
      </Card>

      {/*
        Strengths sit BEFORE the leaks, and they are the same shape of claim: a stat
        inside the band winning players sit in, with the sample it rests on. A section
        that says "you are doing great!" with no number attached is decoration.
      */}
      <Card>
        <h2>What you're doing well at the table</h2>
        {good.length === 0 ? (
          <p className="muted">
            Nothing is far enough inside a healthy range to call it settled yet. A stat
            sitting right on the edge of the band could cross it next session, so this
            waits until it is clear of the line.
          </p>
        ) : (
          <ul className="leaks">
            {good.map((g) => (
              <li key={g.id} className="leak leak-good">
                <h3>{g.title}</h3>
                <p>{g.detail}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <h2>What to work on</h2>
        {leaks.length === 0 ? (
          <p className="muted">
            Every number is inside the range winning small-stakes players sit in.
            Play more hands, or turn the difficulty up, and this will find something.
          </p>
        ) : (
          <>
            {/*
              Three at a time, worst first, with the rest folded away.
              A report that lists nine things is a report nobody acts on: it reads as a
              verdict on the player rather than as something to do next. The findings
              are already ranked, so the top of that ranking is the answer to "what
              should I work on", and the rest is reference.
            */}
            <p className="muted small">
              Worst first. Fix one at a time — they are not independent, and the top of
              this list is usually causing part of the rest.
            </p>
            <ul className="leaks">
              {leaks.slice(0, 3).map((f) => <LeakItem key={f.id} f={f} />)}
            </ul>
            {leaks.length > 3 && (
              <details className="more-leaks">
                <summary>
                  {leaks.length - 3} more {leaks.length - 3 === 1 ? "thing" : "things"} the
                  numbers flagged
                </summary>
                <ul className="leaks">
                  {leaks.slice(3).map((f) => <LeakItem key={f.id} f={f} />)}
                </ul>
              </details>
            )}
          </>
        )}
      </Card>

      {/*
        The loop the two products exist to close: the game finds the leak, the trainer
        is where you fix it. Only shown when the report has actually found something,
        and only pointing at preflop — because that is the street the trainer covers,
        and pointing it anywhere else would be a promise the charts do not keep.
      */}
      {worstStreet(totals) === "preflop" && leaks.length > 0 && (
        <Card>
          <h2>Where to fix it</h2>
          <p>
            Most of the avoidable loss our model can see is happening before the flop,
            which is the one street you can practise away from a table — the decision is
            the same every time.
          </p>
          <p className="muted small">
            The trainer's short-stack charts are solved, not modelled. They cover the
            spots this report is pointing at.
          </p>
          <Link className="btn" to="/train">Open the trainer</Link>
        </Card>
      )}

      <Card>
        <h2>Sessions</h2>
        {/*
          The table scrolls inside this, not by dragging the page.
          Five columns do not fit a 390px phone, and without a scroll container the
          overflow lands on the document: the whole report shifted sideways by 30px
          and every card on the page could be dragged off its own left edge.
        */}
        <div className="table-scroll">
        <table className="sessions">
          <thead>
            <tr><th>When</th><th>Table</th><th>Hands</th><th>Result</th><th>Avoidable</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{new Date(r.ended_at * 1000).toLocaleDateString()}</td>
                <td>{r.seats}-handed {r.format}, {r.stack_bb}bb</td>
                <td className="num">{r.hands}</td>
                <td className={`num ${r.net >= 0 ? "stat-up" : "stat-down"}`}>
                  {r.big_blind > 0 ? `${(r.net / r.big_blind).toFixed(1)} bb` : "—"}
                </td>
                <td className="num">
                  {r.big_blind > 0 ? `${(r.ev_lost_est / r.big_blind).toFixed(1)} bb` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>
    </>
  );
}

function LeakItem({ f }: { f: Finding }) {
  return (
    <li className={`leak leak-${f.severity}`}>
      <h3>{f.title}</h3>
      <p>{f.detail}</p>
      <p className="leak-advice">{f.advice}</p>
      <p className="muted small">From {f.sample} observations.</p>
    </li>
  );
}

function Big({ label, value, tone, unit }: {
  label: string; value: string; tone?: "up" | "down"; unit?: string;
}) {
  return (
    <div className="stat">
      <dt>{label}</dt>
      {/* The unit is its own line: "-9.8 bb/100" wrapped mid-number in the grid. */}
      <dd className={`num${tone ? ` stat-${tone}` : ""}`}>
        {value}{unit && <em className="stat-unit">{unit}</em>}
      </dd>
    </div>
  );
}

function StreetBars({ totals, hands }: { totals: GameTotals; hands: number }) {
  const rows: [string, number][] = [
    ["Preflop", totals.ev_preflop_bb], ["Flop", totals.ev_flop_bb],
    ["Turn", totals.ev_turn_bb], ["River", totals.ev_river_bb],
  ];
  const max = Math.max(...rows.map(([, v]) => v), 1e-9);
  return (
    <ul className="bars">
      {rows.map(([label, v]) => (
        <li key={label}>
          <span className="bar-label">{label}</span>
          <span className="bar-track">
            <span className="bar-fill" style={{ width: `${Math.max(1, (v / max) * 100)}%` }} />
          </span>
          <span className="bar-value num">
            {hands > 0 ? ((v / hands) * 100).toFixed(2) : "0.00"} bb/100
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Which street cost the most, by our model's estimate. Null when nothing has. */
function worstStreet(t: GameTotals): "preflop" | "flop" | "turn" | "river" | null {
  const rows: ["preflop" | "flop" | "turn" | "river", number][] = [
    ["preflop", t.ev_preflop_bb], ["flop", t.ev_flop_bb],
    ["turn", t.ev_turn_bb], ["river", t.ev_river_bb],
  ];
  const best = rows.sort((a, b) => b[1] - a[1])[0];
  return best && best[1] > 0 ? best[0] : null;
}
